import { zodResolver } from "@hookform/resolvers/zod";
import { X } from "lucide-react";
import { useCallback } from "react";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";

import { ImageUploadInput } from "@/components/inputs/ImageInput";
import { PlayerIdInput } from "@/components/inputs/PlayerIdInput";
import { TournamentIdInput } from "@/components/inputs/TournamentIdInput";
import { Paragraph } from "@/components/typography";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    CreateExternalUrlArticleInput,
    CreateRichTextArticleInput,
    CreateStoryArticleInput,
    PlayerTag,
    TournamentTag,
} from "@/gen/graphql";

import ArticleRichTextEditor from "./ArticleRichTextEditor";
import { StoryEdit } from "./StoryEdit";
import { VideoIdInput } from "./VideoInput";
import { ArticleInput, zArticleInput } from "./articleInput";

export type FormData =
    | ({ kind: "rich_text" } & CreateRichTextArticleInput)
    | ({ kind: "external_url" } & CreateExternalUrlArticleInput)
    | ({ kind: "story" } & CreateStoryArticleInput);

function normalizeFormData(data: ArticleInput): FormData {
    return {
        ...data,
        playerTags: data.playerTags.filter(tag => tag.playerId !== null) as PlayerTag[],
        tournamentTags: data.tournamentTags.filter(
            tag => tag.tournamentId !== null
        ) as TournamentTag[],
    };
}

export default function ArticleForm({
    defaultValues,
    editableKind,
    onSubmit,
}: {
    defaultValues?: ArticleInput;
    editableKind?: boolean;
    onSubmit: SubmitHandler<FormData>;
}) {
    const form = useForm<ArticleInput>({
        defaultValues,
        mode: "onBlur",
        resolver: zodResolver(zArticleInput),
    });

    const tournamentTags = useFieldArray({
        control: form.control,
        name: "tournamentTags",
    });
    const playerTags = useFieldArray({
        control: form.control,
        name: "playerTags",
    });

    const storyItems = useFieldArray({
        control: form.control,
        name: "items",
    });

    const submitHandler: SubmitHandler<ArticleInput> = useCallback(
        input => {
            onSubmit(normalizeFormData(input));
        },
        [onSubmit]
    );

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(submitHandler)} className="flex flex-col gap-8">
                <div className="flex flex-row flex-wrap gap-8">
                    <div className="min-w-[600px] flex-1 flex-grow-[3] space-y-4">
                        <FormField
                            control={form.control}
                            name="title"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Title</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Title" {...field} />
                                    </FormControl>
                                    <FormDescription>
                                        The title shown with the article, where applicable.
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="source"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Source</FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="Source"
                                            {...field}
                                            value={field.value ?? ""}
                                        />
                                    </FormControl>
                                    <FormDescription>
                                        The source shown with the article, where applicable.
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="videoId"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Video</FormLabel>
                                    <FormControl>
                                        <VideoIdInput
                                            onVideoIdChange={field.onChange}
                                            videoId={field.value}
                                        />
                                    </FormControl>
                                    <FormDescription>
                                        The video to show with the article.
                                    </FormDescription>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="imageId"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Image</FormLabel>
                                    <FormControl>
                                        <ImageUploadInput
                                            imageId={field.value}
                                            onImageId={field.onChange}
                                        />
                                    </FormControl>
                                    <FormDescription>
                                        The image to show with the article.
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {editableKind && (
                            <FormField
                                control={form.control}
                                name="kind"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Kind</FormLabel>
                                        <FormControl>
                                            <Select
                                                onValueChange={field.onChange}
                                                defaultValue={field.value}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select content type" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="rich_text">
                                                        Rich text
                                                    </SelectItem>
                                                    <SelectItem value="external_url">
                                                        External URL
                                                    </SelectItem>
                                                    <SelectItem value="story">Story</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}

                        {form.watch("kind") === "rich_text" && (
                            <FormField
                                control={form.control}
                                name="content"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Content</FormLabel>
                                        <FormControl>
                                            <ArticleRichTextEditor
                                                initialValue={field.value}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}

                        {form.watch("kind") === "external_url" && (
                            <FormField
                                control={form.control}
                                name="url"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>URL</FormLabel>
                                        <FormControl>
                                            <Input placeholder="URL" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}
                        {form.watch("kind") === "story" && (
                            <StoryEdit form={form} storyItems={storyItems} />
                        )}
                    </div>
                    <aside className="glex-grow-[2] flex min-w-[400px] flex-1 flex-col gap-4">
                        <Card>
                            <CardHeader>
                                <CardTitle>Tournaments</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="mb-4 flex flex-col gap-2">
                                    {tournamentTags.fields.map((tournamentTag, index) => (
                                        <div
                                            key={tournamentTag.id}
                                            className="flex flex-row items-center gap-2"
                                        >
                                            <FormField
                                                control={form.control}
                                                name={`tournamentTags.${index}.tournamentId`}
                                                render={({ field }) => (
                                                    <TournamentIdInput
                                                        className="max-w-[500px] flex-1"
                                                        tournamentId={field.value}
                                                        onTournamentIdChange={field.onChange}
                                                    />
                                                )}
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                className="px-2"
                                                onClick={() => tournamentTags.remove(index)}
                                            >
                                                <X />
                                            </Button>
                                        </div>
                                    ))}
                                    {tournamentTags.fields.length === 0 && (
                                        <Paragraph>
                                            <i>No tournaments tagged.</i>
                                        </Paragraph>
                                    )}
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => tournamentTags.append({ tournamentId: null })}
                                >
                                    Add tournament tag
                                </Button>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader>
                                <CardTitle>Players</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="mb-4 flex flex-col gap-2">
                                    {playerTags.fields.map((playerTag, index) => (
                                        <div
                                            key={playerTag.id}
                                            className="flex flex-row items-center gap-2"
                                        >
                                            <FormField
                                                control={form.control}
                                                name={`playerTags.${index}.playerId`}
                                                render={({ field }) => (
                                                    <PlayerIdInput
                                                        className="max-w-[500px] flex-1"
                                                        playerId={field.value}
                                                        onPlayerIdChange={field.onChange}
                                                    />
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name={`playerTags.${index}.kind`}
                                                render={({ field }) => (
                                                    <Select
                                                        value={field.value}
                                                        onValueChange={field.onChange}
                                                    >
                                                        <SelectTrigger className="w-[100px]">
                                                            <SelectValue placeholder="Kind" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="FEATURE">
                                                                Feature
                                                            </SelectItem>
                                                            <SelectItem value="MENTION">
                                                                Mention
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                className="px-2"
                                                onClick={() => playerTags.remove(index)}
                                            >
                                                <X />
                                            </Button>
                                        </div>
                                    ))}
                                    {playerTags.fields.length === 0 && (
                                        <Paragraph>
                                            <i>No players tagged.</i>
                                        </Paragraph>
                                    )}
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() =>
                                        playerTags.append({ playerId: null, kind: "MENTION" })
                                    }
                                >
                                    Add player tag
                                </Button>
                            </CardContent>
                        </Card>
                    </aside>
                </div>

                <Button
                    type="submit"
                    disabled={!form.formState.isValid || form.formState.isSubmitting}
                >
                    Save
                </Button>
            </form>
        </Form>
    );
}
